import {
  Eye, Users, Building2, FolderOpen, Settings, DollarSign, Calendar, User, Shield,
  Package, FileSignature, FileText, ClipboardCheck, ListChecks, PackageCheck, MapPin,
  BookOpen, Cog, Zap, AlertTriangle, MessageSquare, History, Paperclip, Truck,
  LayoutDashboard, GraduationCap, Star,
} from 'lucide-react';
import { ALL_SETTINGS_VIEW_KEYS } from './settingsTabs';

// Icon names are stored on the shortcut document, so this map is the
// contract — never rename a key, only add.
export const SHORTCUT_ICONS = {
  dashboard: LayoutDashboard,
  overview: Eye,
  employees: Users,
  profile: User,
  customers: Building2,
  projects: FolderOpen,
  checklists: ClipboardCheck,
  tasks: ListChecks,
  timesheets: Calendar,
  budget: DollarSign,
  invoices: FileText,
  manpower: Users,
  iso: Shield,
  contracts: FileSignature,
  inventory: Package,
  reports: FileText,
  settings: Settings,
  company: Building2,
  assets: Package,
  deliveries: PackageCheck,
  assignments: MapPin,
  catalogue: BookOpen,
  suppliers: Truck,
  machines: Cog,
  testReports: Zap,
  damage: AlertTriangle,
  surveys: MessageSquare,
  audit: History,
  attachments: Paperclip,
  training: GraduationCap,
  star: Star,
};

export const resolveShortcutIcon = (name) => SHORTCUT_ICONS[name] || Star;

// Auto colour: stable per icon family so a project's Checklists tile looks
// the same shade wherever it was pinned from.
const PALETTE = {
  slate: 'bg-slate-100 text-slate-700',
  blue: 'bg-blue-100 text-blue-700',
  emerald: 'bg-emerald-100 text-emerald-700',
  amber: 'bg-amber-100 text-amber-700',
  violet: 'bg-violet-100 text-violet-700',
  rose: 'bg-rose-100 text-rose-700',
  cyan: 'bg-cyan-100 text-cyan-700',
  indigo: 'bg-indigo-100 text-indigo-700',
};
const COLOR_KEYS = Object.keys(PALETTE);

export const shortcutColorClass = (color) => PALETTE[color] || PALETTE.slate;

export const autoColor = (seed = '') => {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) % 9973;
  return COLOR_KEYS[h % COLOR_KEYS.length];
};

// Main tab → icon, label key and the permissions the tab itself is gated on.
export const MAIN_TAB_META = {
  dashboard: { icon: 'overview', labelKey: 'nav.dashboard', perms: [] },
  employees: { icon: 'employees', labelKey: 'nav.employees', perms: ['employees.view'] },
  'my-profile': { icon: 'profile', labelKey: 'nav.profile', perms: [] },
  customers: { icon: 'customers', labelKey: 'nav.customers', perms: ['customers.view'] },
  projects: { icon: 'projects', labelKey: 'nav.projects', perms: ['projects.view'] },
  'weekly-timesheets': { icon: 'timesheets', labelKey: 'nav.timesheets', perms: ['timesheets.view'] },
  budget: { icon: 'budget', labelKey: 'nav.budget', perms: ['budget.view'] },
  invoices: { icon: 'invoices', labelKey: 'nav.invoices', perms: ['invoices.view'] },
  manpower: { icon: 'manpower', labelKey: 'nav.manpower', perms: ['manpower.view'] },
  iso9001: {
    icon: 'iso',
    labelKey: 'nav.iso',
    perms: ['iso_documents.view', 'iso_checklists.view', 'iso_test_reports.view'],
  },
  contracts: {
    icon: 'contracts',
    labelKey: 'nav.contracts',
    perms: ['contracts.view', 'contract_templates.view', 'client_stamp_signatories.view'],
  },
  inventory: { icon: 'inventory', labelKey: 'nav.inventory', perms: ['inventory.view'] },
  reports: { icon: 'reports', labelKey: 'nav.reports', perms: ['reports.view'] },
  company: { icon: 'company', labelKey: 'nav.company', perms: [] },
  settings: { icon: 'settings', labelKey: 'nav.settings', perms: ALL_SETTINGS_VIEW_KEYS },
};

export const INVENTORY_TAB_META = {
  dashboard: { icon: 'dashboard', labelKey: 'inv.tabs.dashboard', perms: ['inventory_dashboard.view'] },
  assets: { icon: 'assets', labelKey: 'inv.tabs.assets', perms: ['inventory.view'] },
  assignments: { icon: 'assignments', labelKey: 'inv.tabs.assignments', perms: ['inventory_assignments.view'] },
  suppliers: { icon: 'suppliers', labelKey: 'inv.tabs.suppliers', perms: ['inventory_suppliers.view'] },
  deliveries: { icon: 'deliveries', labelKey: 'inv.tabs.deliveries', perms: ['inventory.view'] },
  catalogue: { icon: 'catalogue', labelKey: 'inv.tabs.catalogue', perms: ['inventory_catalogue.view'] },
  settings: { icon: 'settings', labelKey: 'inv.tabs.settings', perms: ['inventory_settings.view'] },
};

export const ISO_TAB_META = {
  dashboard: { icon: 'dashboard', labelKey: 'iso9001.dashboard', perms: [] },
  documents: { icon: 'reports', labelKey: 'iso9001.documents', perms: ['iso_documents.view'] },
  checklists: { icon: 'checklists', labelKey: 'iso9001.checklists', perms: ['iso_checklists.view'] },
  'test-reports': { icon: 'testReports', labelKey: 'iso9001.testReports', perms: ['iso_test_reports.view'] },
  'customer-surveys': { icon: 'surveys', labelKey: 'iso9001.customerSurveys', perms: [] },
  'ncr-capa': { icon: 'damage', labelKey: 'iso9001.ncrCapa', perms: [] },
  audits: { icon: 'audit', labelKey: 'iso9001.audits', perms: [] },
  training: { icon: 'training', labelKey: 'iso9001.training', perms: [] },
  employees: { icon: 'employees', labelKey: 'iso9001.employees', perms: [] },
  ratings: { icon: 'star', labelKey: 'iso9001.ratings', perms: [] },
};

export const PROJECT_TAB_META = {
  details: { icon: 'projects', labelKey: 'projects_deep.tabs.details', perms: ['projects.view'] },
  location: { icon: 'assignments', labelKey: 'projects_deep.tabs.location', perms: ['projects.view'] },
  tasks: { icon: 'tasks', labelKey: 'projects_deep.tabs.tasks', perms: ['projects.view'] },
  checklists: { icon: 'checklists', labelKey: 'projects_deep.tabs.checklists', perms: ['projects.view'] },
  machines: { icon: 'machines', label: 'Machines', perms: ['projects.view'] },
  'test-reports': { icon: 'testReports', labelKey: 'projects_deep.tabs.testReports', perms: ['projects.view'] },
  'damage-reports': { icon: 'damage', labelKey: 'projects_deep.tabs.damage', perms: ['projects.view'] },
  attachments: { icon: 'attachments', labelKey: 'projects_deep.tabs.attachments', perms: ['projects.view'] },
  contracts: { icon: 'contracts', labelKey: 'projects_deep.tabs.documents', perms: ['contracts.view'] },
  'hired-plant': { icon: 'budget', label: 'Hired plant', perms: ['hire_costs.view'] },
  surveys: { icon: 'surveys', labelKey: 'projects_deep.tabs.surveys', perms: ['projects.view'] },
  audit: { icon: 'audit', labelKey: 'projects_deep.tabs.audit', perms: ['projects.view'] },
};

export const metaLabel = (meta, t) => (meta?.labelKey ? t(meta.labelKey) : meta?.label || '');

// "Quick create" shortcuts: land on the right screen AND open its new-record
// form (the receiving component watches `navContext.create`).
export const QUICK_ACTIONS = [
  {
    key: 'checklist', icon: 'checklists', labelKey: 'shortcuts.qc.checklist',
    tab: 'projects', needsProject: true, perms: ['projects.view'],
    ctx: (pid) => ({ openProjectId: pid, projectTab: 'checklists', create: 'checklist' }),
  },
  {
    key: 'task', icon: 'tasks', labelKey: 'shortcuts.qc.task',
    tab: 'projects', needsProject: true, perms: ['projects.view'],
    ctx: (pid) => ({ openProjectId: pid, projectTab: 'tasks', create: 'task' }),
  },
  {
    key: 'test_report', icon: 'testReports', labelKey: 'shortcuts.qc.testReport',
    tab: 'projects', needsProject: true, perms: ['projects.view'],
    ctx: (pid) => ({ openProjectId: pid, projectTab: 'test-reports', create: 'test_report' }),
  },
  {
    key: 'damage_report', icon: 'damage', labelKey: 'shortcuts.qc.damageReport',
    tab: 'projects', needsProject: true, perms: ['projects.view'],
    ctx: (pid) => ({ openProjectId: pid, projectTab: 'damage-reports', create: 'damage_report' }),
  },
  {
    key: 'asset', icon: 'assets', labelKey: 'shortcuts.qc.asset',
    tab: 'inventory', perms: ['inventory.edit'],
    ctx: () => ({ invSubTab: 'assets', create: 'asset' }),
  },
  {
    key: 'delivery', icon: 'deliveries', labelKey: 'shortcuts.qc.delivery',
    tab: 'inventory', perms: ['inventory.edit'],
    ctx: () => ({ invSubTab: 'deliveries', create: 'delivery' }),
  },
  {
    key: 'iso_checklist', icon: 'checklists', labelKey: 'shortcuts.qc.isoChecklist',
    tab: 'iso9001', perms: ['iso_checklists.view'],
    ctx: () => ({ isoSubTab: 'checklists', create: 'iso_checklist' }),
  },
  {
    key: 'timesheet', icon: 'timesheets', labelKey: 'shortcuts.qc.timesheet',
    tab: 'weekly-timesheets', perms: ['timesheets.view'],
    ctx: () => ({ create: 'timesheet' }),
  },
];
